import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { useAuth } from "../../contexts/AuthContext";
import { Link } from "react-router-dom";
import { FileText, Loader2, Play, Pause, Zap, CheckCircle, AlertTriangle, Shield } from "lucide-react";
import CompoundROIWidget from "./CompoundROIWidget";

const RHYTHM_LABEL = {
  slow: "1 / day",
  medium: "3 / day",
  turbo: "10 / day (Turbo)",
};

/**
 * Dashboard tab: "Blog Packs" — user's owned packs with status, progress, rhythm config.
 */
export default function BlogPacksPanel() {
  const { user } = useAuth();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState({}); // pack_id -> form state

  const load = () => {
    if (!user?.email) return;
    setLoading(true);
    axios
      .get(`${API}/blog-packs/my?email=${encodeURIComponent(user.email)}`)
      .then((r) => setPacks(r.data.packs || []))
      .finally(() => setLoading(false));
  };

  useEffect(load, [user?.email]);

  const configure = async (pack) => {
    const f = cfg[pack.pack_id || pack._id] || {};
    if (!f.url || !f.url.trim()) {
      alert("Please enter your website URL");
      return;
    }
    try {
      await axios.post(`${API}/blog-packs/configure`, {
        email: user.email,
        pack_id: pack.pack_id || "",
        url: f.url.trim(),
        niche: f.niche || "",
        language: f.language || "en",
        rhythm: f.rhythm || "medium",
      });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Setup failed");
    }
  };

  const updateRhythm = async (pack, rhythm) => {
    try {
      await axios.post(`${API}/blog-packs/rhythm`, {
        email: user.email,
        pack_id: pack.pack_id,
        rhythm,
      });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Update failed");
    }
  };

  const toggle = async (pack) => {
    const url = pack.status === "paused" ? "/blog-packs/resume" : "/blog-packs/pause";
    try {
      await axios.post(
        `${API}${url}?email=${encodeURIComponent(user.email)}&pack_id=${encodeURIComponent(pack.pack_id)}`
      );
      load();
    } catch {}
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!packs.length) {
    return (
      <div className="p-8 text-center border border-dashed border-gray-300 rounded-xl bg-white">
        <FileText className="w-10 h-10 mx-auto mb-3 text-gray-400" />
        <p className="font-bold text-gray-900 mb-2">No blog packs yet</p>
        <p className="text-sm text-gray-500 mb-5">
          Get 25 / 50 / 100 SEO-optimized articles auto-published to your domain.
        </p>
        <Link
          to="/blog-packs"
          data-testid="empty-buy-packs"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700"
        >
          See pricing →
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="blog-packs-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-black text-gray-900">Your Blog Packs</h3>
        <Link
          to="/blog-packs"
          className="text-xs font-bold text-indigo-600 hover:text-indigo-700"
        >
          + Buy more
        </Link>
      </div>

      {/* Compound ROI projection — retention lever, shown whenever user has any pack */}
      <CompoundROIWidget packs={packs} />

      {packs.map((pack, idx) => {
        const id = pack.pack_id || `pending-${idx}`;
        const f = cfg[id] || {};
        const progress = pack.pack_size ? Math.round(((pack.pack_size - pack.remaining) / pack.pack_size) * 100) : 0;
        const needsSetup = pack.status === "pending_setup";

        return (
          <div
            key={id}
            data-testid={`pack-row-${idx}`}
            className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-bold text-gray-900">
                    {pack.pack_size} articles
                    {pack.is_subscription && (
                      <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">
                        Subscription
                      </span>
                    )}
                    {pack.turbo && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                        <Zap className="w-3 h-3" /> Turbo
                      </span>
                    )}
                    {pack.admin_bypass && (
                      <span
                        className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-purple-100 text-purple-700"
                        data-testid={`pack-admin-bypass-${idx}`}
                        title="Bypassed Stripe — activated free for admin testing"
                      >
                        <Shield className="w-3 h-3" /> Admin Free
                      </span>
                    )}
                  </p>
                </div>
                {pack.url && <p className="text-xs text-gray-500">→ {pack.url}</p>}
                {pack.rhythm && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Rhythm: <span className="font-bold">{RHYTHM_LABEL[pack.rhythm] || pack.rhythm}</span>
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end">
                <span className={`text-xs font-bold px-2 py-1 rounded ${
                  pack.status === "active" ? "bg-emerald-100 text-emerald-700" :
                  pack.status === "paused" ? "bg-yellow-100 text-yellow-700" :
                  pack.status === "completed" ? "bg-gray-100 text-gray-700" :
                  "bg-indigo-100 text-indigo-700"
                }`}>
                  {pack.status === "pending_setup" ? "⚙️ Setup needed" :
                   pack.status === "active" ? "✓ Active" :
                   pack.status === "paused" ? "⏸ Paused" :
                   pack.status === "completed" ? "✅ Completed" : pack.status}
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  {pack.remaining}/{pack.pack_size} left
                </span>
              </div>
            </div>

            {/* Progress bar */}
            {!needsSetup && (
              <div className="mt-3">
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-indigo-600 transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-[10px] text-gray-500 mt-1">{progress}% published</p>
              </div>
            )}

            {/* Setup form */}
            {needsSetup && (
              <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
                <div className="flex items-center gap-2 text-amber-700 text-xs font-bold">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Configure your domain + rhythm to start generation
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="https://yourdomain.com"
                    value={f.url || ""}
                    onChange={(e) => setCfg({ ...cfg, [id]: { ...f, url: e.target.value } })}
                    data-testid={`pack-url-${idx}`}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-600"
                  />
                  <input
                    type="text"
                    placeholder="Niche (e.g. running shoes)"
                    value={f.niche || ""}
                    onChange={(e) => setCfg({ ...cfg, [id]: { ...f, niche: e.target.value } })}
                    data-testid={`pack-niche-${idx}`}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-600"
                  />
                </div>
                <div className="grid sm:grid-cols-3 gap-2">
                  {["slow", "medium", "turbo"].map((r) => {
                    const disabled = r === "turbo" && !pack.turbo;
                    const selected = (f.rhythm || "medium") === r;
                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() => !disabled && setCfg({ ...cfg, [id]: { ...f, rhythm: r } })}
                        data-testid={`pack-rhythm-${idx}-${r}`}
                        disabled={disabled}
                        className={`px-3 py-2 text-xs font-bold rounded-lg border transition-colors ${
                          selected
                            ? "bg-indigo-600 text-white border-indigo-600"
                            : disabled
                            ? "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
                            : "bg-white text-gray-700 border-gray-300 hover:border-indigo-600"
                        }`}
                      >
                        {RHYTHM_LABEL[r]}{disabled ? " · requires Turbo" : ""}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => configure(pack)}
                  data-testid={`pack-start-${idx}`}
                  className="w-full px-4 py-2.5 bg-indigo-600 text-white text-sm font-bold rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  Start generation
                </button>
              </div>
            )}

            {/* Active controls */}
            {!needsSetup && pack.status !== "completed" && (
              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
                <select
                  value={pack.rhythm || "medium"}
                  onChange={(e) => updateRhythm(pack, e.target.value)}
                  data-testid={`pack-rhythm-select-${idx}`}
                  className="text-xs border border-gray-300 rounded px-2 py-1.5"
                >
                  <option value="slow">1 / day</option>
                  <option value="medium">3 / day</option>
                  <option value="turbo" disabled={!pack.turbo}>10 / day (Turbo)</option>
                </select>
                <button
                  onClick={() => toggle(pack)}
                  data-testid={`pack-toggle-${idx}`}
                  className="text-xs font-bold px-3 py-1.5 rounded border border-gray-300 hover:border-indigo-600 flex items-center gap-1"
                >
                  {pack.status === "paused" ? (
                    <><Play className="w-3 h-3" /> Resume</>
                  ) : (
                    <><Pause className="w-3 h-3" /> Pause</>
                  )}
                </button>
                {!pack.turbo && (
                  <Link
                    to="/blog-packs"
                    className="text-xs font-bold text-amber-600 hover:text-amber-700 inline-flex items-center gap-1"
                  >
                    <Zap className="w-3 h-3" /> Add Turbo (+€10)
                  </Link>
                )}
              </div>
            )}

            {pack.status === "completed" && (
              <div className="mt-3 flex items-center gap-2 text-xs font-bold text-emerald-700">
                <CheckCircle className="w-4 h-4" />
                All {pack.pack_size} articles published — buy another pack to keep going
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
